import React, { useState, useEffect } from 'react';
import { messageService, Message } from '../../services/messageService';
import { adminService } from '../../services/adminService';
import { Send, Users, User, Bell, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';

const AdminMessaging: React.FC = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'inbox' | 'compose'>('inbox');

    // Compose state
    const [recipientType, setRecipientType] = useState<'single' | 'broadcast'>('single');
    const [recipientId, setRecipientId] = useState('');
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');

    useEffect(() => {
        loadMessages();
    }, []);

    const loadMessages = async () => {
        setLoading(true);
        const inbox = await messageService.getAdminInbox();
        setMessages(inbox);
        setLoading(false);
    };

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!subject || !content) return;
        if (recipientType === 'single' && !recipientId) {
            toast.error('User ID is required for direct messages');
            return;
        }

        try {
            if (recipientType === 'broadcast') {
                if (!window.confirm('Are you sure you want to message ALL users?')) return;
                await messageService.broadcastMessage(subject, content);
                toast.success('Broadcast sent successfully');
            } else {
                await messageService.sendMessageToUser(recipientId, subject, content);
                toast.success('Message sent');
            }

            // Reset form
            setSubject('');
            setContent('');
            setRecipientId('');
            setActiveTab('inbox');
        } catch (error) {
            toast.error('Failed to send message');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900 border-l-4 border-red-500 pl-3">Messaging System</h1>
                <div className="flex gap-2 bg-white rounded-lg p-1 border">
                    <button
                        onClick={() => setActiveTab('inbox')}
                        className={`px-4 py-2 rounded text-sm font-medium flex items-center gap-2 ${activeTab === 'inbox' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-gray-50'}`}
                    >
                        <Mail size={16} /> Inbox
                    </button>
                    <button
                        onClick={() => setActiveTab('compose')}
                        className={`px-4 py-2 rounded text-sm font-medium flex items-center gap-2 ${activeTab === 'compose' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-gray-50'}`}
                    >
                        <Send size={16} /> Compose
                    </button>
                </div>
            </div>

            {activeTab === 'inbox' ? (
                <div className="bg-white rounded-lg shadow border border-gray-200">
                    <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                        <h3 className="font-semibold text-gray-700">Messages from Users</h3>
                        <button onClick={loadMessages} className="p-2 hover:bg-gray-200 rounded-full"><RefreshCw size={14} /></button>
                    </div>
                    <div className="divide-y divide-gray-100 max-h-[600px] overflow-auto">
                        {loading ? (
                            <div className="p-8 text-center text-gray-500">Loading messages...</div>
                        ) : messages.length === 0 ? (
                            <div className="p-12 text-center text-gray-400 flex flex-col items-center">
                                <Mail size={48} className="mb-2 opacity-20" />
                                <p>No messages found</p>
                            </div>
                        ) : (
                            messages.map(msg => (
                                <div key={msg.id} className={`p-4 hover:bg-gray-50 transition cursor-pointer ${!msg.read ? 'bg-blue-50/50' : ''}`}>
                                    <div className="flex justify-between mb-1">
                                        <div className="font-semibold text-gray-900">{msg.senderName} <span className="text-xs font-normal text-gray-500">({msg.senderId})</span></div>
                                        <div className="text-xs text-gray-500">
                                            {msg.createdAt?.seconds ? new Date(msg.createdAt.seconds * 1000).toLocaleDateString() : 'Just now'}
                                        </div>
                                    </div>
                                    <div className="font-medium text-sm text-gray-800 mb-1">{msg.subject}</div>
                                    <p className="text-sm text-gray-600 line-clamp-2">{msg.content}</p>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow border border-gray-200 max-w-2xl mx-auto">
                    <div className="p-6">
                        <form onSubmit={handleSend} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Recipient Type</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setRecipientType('single')}
                                        className={`p-3 rounded border text-center flex flex-col items-center gap-2 ${recipientType === 'single' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50'}`}
                                    >
                                        <User size={20} />
                                        <span className="text-sm font-medium">Single User</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setRecipientType('broadcast')}
                                        className={`p-3 rounded border text-center flex flex-col items-center gap-2 ${recipientType === 'broadcast' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 hover:bg-gray-50'}`}
                                    >
                                        <Users size={20} />
                                        <span className="text-sm font-medium">Evaluate & Broadcast</span>
                                    </button>
                                </div>
                            </div>

                            {recipientType === 'single' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                                    <input
                                        type="text"
                                        value={recipientId}
                                        onChange={(e) => setRecipientId(e.target.value)}
                                        className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="Paste user ID here..."
                                        required
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                                <input
                                    type="text"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                    placeholder="Message subject..."
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                                <textarea
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 outline-none min-h-[150px]"
                                    placeholder="Write your secure message..."
                                    required
                                />
                            </div>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    className={`w-full py-3 rounded text-white font-medium flex items-center justify-center gap-2 ${recipientType === 'broadcast' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                                >
                                    <Send size={18} />
                                    {recipientType === 'broadcast' ? 'Broadcast to ALL Users' : 'Send Secure Message'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminMessaging;
